import DoneIcon from '@mui/icons-material/Done';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, styled } from '@mui/material';
import type { ButtonProps } from '@mui/material/Button';
import _ from 'lodash';
import type { FC, HTMLAttributes, PropsWithChildren } from 'react';

import { goPalette } from '../../../material-ui-theme/tsGoThemeVars';
import { useZustand } from '../../../zustandStore';

const DialogText = styled(DialogContentText)(({ theme }) => ({
	display: 'flex',
	flexWrap: 'wrap',
	color: theme.palette.grey[900],
	padding: theme.spacing(1),
	textAlign: 'center',
	...theme.typography.body1,
}));

const ColorButton = styled(Button)<ButtonProps>(() => {
	return {
		color: 'white',
		backgroundColor: goPalette.tsGoColor.pink,
		'&:hover': goPalette.tsGoColor.pinkDarker,
	};
});

type AlertDialogProps = HTMLAttributes<HTMLElement>;

const AlertDialog: FC<PropsWithChildren<AlertDialogProps>> = () => {
	const dialog = useZustand(state => state.dialog.alert);
	const showAlert = useZustand(state => state.dialog.alert.open);
	const { resetDialog, closeDialog } = useZustand();

	const getTitle = () => {
		switch (dialog.type) {
			case 'warning': {
				return <WarningIcon fontSize="large" color="warning" />;
			}
			case 'success': {
				return <DoneIcon fontSize="large" color="success" />;
			}
			case 'error': {
				return <ErrorIcon fontSize="large" color="error" />;
			}
			default: {
				return <InfoIcon fontSize="large" color="info" />;
			}
		}
	};

	return (
		<Dialog
			open={showAlert}
			aria-labelledby="alert-dialog-title"
			aria-describedby="alert-dialog-description"
			onClose={() => resetDialog()}
			data-testid="dialog"
		>
			<DialogContent>
				<Box
					component="div"
					sx={{
						margin: 1,
						display: 'flex',
						justifyContent: 'center',
					}}
				>
					{getTitle()}
				</Box>
				<DialogText data-testid="alert-dialog-description" id="alert-dialog-description">
					{dialog.message}
				</DialogText>
			</DialogContent>
			<DialogActions sx={{ justifyContent: 'center', margin: 1 }}>
				<ColorButton
					data-testid="alert-dialog-action-button"
					onClick={() => {
						closeDialog();
						if (typeof dialog.onAccept === 'function') {
							dialog.onAccept();
						}
					}}
					variant="contained"
					color={dialog.type || 'info'}
				>
					{dialog.actionText}
				</ColorButton>
				{dialog.showCancel ? (
					<Button
						data-testid="alert-dialog-cancel-button"
						onClick={() => resetDialog()}
						variant="contained"
						color="secondary"
					>
						{dialog.cancelText || 'Cancel'}
					</Button>
				) : (
					''
				)}
			</DialogActions>
		</Dialog>
	);
};

export default AlertDialog;
